// GRUNNBILDET UT TIL GNÅ — konvolutten feltappen leser.
//
// ── ⚠ EIERBESLUTNING 2026-08-18 ─────────────────────────────────────────────
//   Spørsmål: skal den estimerte politiposisjonen deles til GNÅ?
//   Svar: «Ja … men i GNÅ skal nyheten droppes etter en uke.»
//
// Jeg foreslo at GNÅ bare skulle få hendelser med presisjon `punkt` eller
// menneskerettet posisjon, fordi en operatør i COP kan lese en usikkerhetssirkel
// mens en telefon i bevegelse er et dårligere sted for et forbehold. Eier valgte
// at de skal deles uansett — og da flyttes ansvaret til HVORDAN de vises, ikke OM.
//
// Derfor bærer konvolutten usikkerheten som et FELT feltappen må forholde seg til,
// ikke som noe den kan velge å ignorere: `usikkerhet_m` og `presisjon` er
// obligatoriske i utsendingen. En klient som tegner en pin uten dem, tegner en
// løgn — og da er det klientens feil, ikke kontraktens.
//
// ── ⚠ TO LEVETIDER, ULIKE PER FLATE ────────────────────────────────────────
//   SKADE: grå etter 1 døgn, ute av bildet etter 1 uke (`POLITI_FORELDET_MS`,
//          `POLITI_LEVETID_MS`).
//   GNÅ:   droppet etter 1 uke.
//
// Uka er derfor det SAMME tallet på begge flatene, og det er ikke tilfeldig: to
// ulike tall ville betydd at en hendelse fortsatt sto på en soldats telefon etter at
// TOC-en hadde mistet den — altså at felten handlet på noe staben ikke lenger så.
// `GNA_GRUNNBILDE_LEVETID_MS` er den ene kilden, og den leses av begge sider.
import { z } from 'zod';
import { POLITI_LEVETID_MS } from './politiloggen.js';
/** Konvolutt-type i Matrix-meldingskroppen. Speiler mønsteret i `geofence.ts`. */
export const GNA_GRUNNBILDE_TYPE = 'no.gna.skade.grunnbilde';
export const GNA_GRUNNBILDE_VERSJON = 1;
/**
 * Hvor lenge en åpen-kilde-hendelse lever i GNÅ.
 *
 * ⚠ SAMME TALL SOM SKADE-LEVETIDEN, og det er en sikkerhetsegenskap: var GNÅ lengre,
 * ville en soldat hatt en hendelse på telefonen som TOC-en ikke lenger så — og da
 * handler felten på noe staben ikke kan følge opp. Var GNÅ kortere, ville staben
 * trodd felten hadde informasjon hun ikke hadde.
 */
export const GNA_GRUNNBILDE_LEVETID_MS = POLITI_LEVETID_MS;
/**
 * Hendelsen slik GNÅ leser den.
 *
 * ⚠ SNAKE_CASE OG KORTE NØKLER. Dette er wire-formatet mot Dart, og resten av
 * GNÅ-broen (teig, posisjon, bilde) bruker samme konvensjon. Å bruke camelCase her
 * ville krevd en oversetter på Dart-siden — og den oversetteren er der felt blir
 * glemt.
 */
export const GnaGrunnbildeSchema = z.object({
    id: z.string().max(200),
    /** Kategori/type, kildens eget ord («Trafikk», «Brann»). */
    type: z.string().max(64),
    tittel: z.string().max(300),
    utdrag: z.string().max(600).default(''),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** Da hendelsen SKJEDDE (ms). GNÅ regner alder og utløp fra dette. */
    ts_ms: z.number(),
    /**
     * Usikkerhetsradius i meter. ⚠ PÅKREVD, IKKE VALGFRI.
     *
     * 0 betyr et ekte punkt. Alt annet SKAL tegnes som en sirkel eller et
     * flate-symbol i GNÅ — aldri som en pin. Gjorde vi feltet valgfritt, ville en
     * klient som ikke leste det tegnet 30 km usikkerhet som en adresse.
     */
    usikkerhet_m: z.number().nonnegative(),
    /** Presisjonsnivå, så klienten kan velge symbol uten å regne på radiusen. */
    presisjon: z.enum(['punkt', 'naeromraade', 'kommune', 'fylke', 'omraade', 'ukjent']),
    /**
     * Polygonringer når varselet gjelder en FLATE (MET-farevarsel).
     *
     * ⚠ GNÅ SKAL TEGNE FLATA, ikke bare markøren. Et skredvarsel tegnet som en pin
     * sier at faren er der og ikke i resten av området — og på en telefon i felt er
     * det den feilen som kan sende noen inn i skredterrenget.
     *
     * Utelatt for punkthendelser. ⚠ Kappet til 500 punkt per ring i utsendingen: en
     * MET-polygon kan ha tusenvis, og en Matrix-melding på hundre kilobyte drenerer
     * batteri og båndbredde på en telefon som skal vare i tre døgn.
     */
    omraade: z.array(z.array(z.object({ lat: z.number(), lon: z.number() })).max(500)).max(20).optional(),
    /** Kildens navn, til attribusjon i grensesnittet. */
    kilde: z.string().max(120),
    /** Lisensstreng. ⚠ Følger hendelsen fordi vilkårene gjelder også i felt. */
    lisens: z.string().max(200).default(''),
    /** Lenke til kilden. Opphavsrett: GNÅ viser utdrag + lenke, aldri hele saken. */
    url: z.string().max(2048).optional(),
    /** Når GNÅ skal slutte å vise den (ms). Absolutt tid, ikke en varighet. */
    utloper_ms: z.number(),
    /** Satt når en operatør har rettet posisjonen — da er den ikke et estimat. */
    rettet_av: z.string().max(128).optional(),
});
/**
 * Bygg konvoluttkroppen GNÅ leser.
 *
 * `null` når hendelsen ikke kan deles — og de to grunnene er ulike:
 *   • ingen posisjon: GNÅ er en KARTflate, en hendelse uten sted har ingen plass
 *   • alt utløpt: å sende noe som er dødt ved ankomst er bare støy
 */
export function gnaGrunnbildeBody(o, naaMs) {
    if (o.lat === undefined || o.lon === undefined)
        return null;
    const utloperMs = o.tsMs + GNA_GRUNNBILDE_LEVETID_MS;
    if (utloperMs <= naaMs)
        return null;
    const h = {
        id: o.id,
        type: o.type,
        tittel: o.tittel,
        utdrag: o.utdrag,
        lat: o.lat,
        lon: o.lon,
        ts_ms: o.tsMs,
        // ⚠ 0 NÅR VI IKKE HAR ET TALL, og det er trygt HER fordi presisjonen sendes
        // med: en klient som ser `presisjon: 'kommune'` med `usikkerhet_m: 0` har en
        // motsigelse den skal behandle som usikkerhet, ikke som et punkt. Se
        // `gnaTegnesSomOmraade`.
        usikkerhet_m: o.usikkerhetM ?? 0,
        presisjon: o.presisjon ?? 'ukjent',
        // ⚠ FORENKLER RINGENE FOR EN TELEFON. En MET-polygon kan ha tusenvis av punkt;
        // en melding på hundre kilobyte drenerer batteri og båndbredde på et samband som
        // skal vare i tre døgn. `forenkleRing` beholder formen og tar hvert n-te punkt —
        // en flate som er 200 m unna den ekte kanten er godt nok for «hold deg unna dette
        // området», og langt bedre enn ingen flate.
        // ⚠ `.map((r) => forenkleRing(r))`, ALDRI `.map(forenkleRing)`. `Array.map`
        // sender (element, INDEKS, array), så den korte formen ga `maks = 0` på første
        // ring, negativt steg, og en indeks som gikk under null — altså en krasj i
        // hubben på hver MET-utsending. En test fanget det.
        ...(o.omraade?.length ? { omraade: o.omraade.slice(0, 20).map((r) => forenkleRing(r)) } : {}),
        kilde: o.proveniens.kildeNavn,
        lisens: o.proveniens.lisens,
        ...(o.proveniens.url ? { url: o.proveniens.url } : {}),
        utloper_ms: utloperMs,
        ...(o.flyttetAv ? { rettet_av: o.flyttetAv.av } : {}),
    };
    return JSON.stringify({ t: GNA_GRUNNBILDE_TYPE, v: GNA_GRUNNBILDE_VERSJON, op: 'upsert', hendelse: h });
}
/**
 * Tynn ut en ring til maks `maks` punkter, med formen intakt.
 *
 * ⚠ BEHOLDER ALLTID FØRSTE OG SISTE PUNKT. Uten det kunne ringen bli åpen, og en
 * åpen ring tegnes ikke som en flate — altså ville varselet blitt en strek.
 */
export function forenkleRing(ring, maks = 500) {
    // ⚠ NEDRE GRENSE PÅ 3. En ring kan ikke bli en flate med færre punkter, og et
    // `maks` på 0 eller 1 ga tidligere negativt steg og en indeks under null. Vakta er
    // ikke bare defensiv: den gjør at et feilkall degraderer til «grovest mulige
    // trekant» framfor å velte utsendingen.
    const tak = Math.max(3, Math.floor(maks) || 3);
    if (ring.length <= tak)
        return ring.map((p) => ({ lat: p.lat, lon: p.lon }));
    const steg = Math.max(1, Math.ceil(ring.length / (tak - 1)));
    const ut = [];
    for (let i = 0; i < ring.length; i += steg)
        ut.push({ lat: ring[i].lat, lon: ring[i].lon });
    const siste = ring[ring.length - 1];
    const sist = ut[ut.length - 1];
    if (!sist || sist.lat !== siste.lat || sist.lon !== siste.lon) {
        ut.push({ lat: siste.lat, lon: siste.lon });
    }
    return ut;
}
/** Slettekonvolutt — brukt når kilden har TRUKKET meldingen. */
export function gnaGrunnbildeSlettBody(id) {
    return JSON.stringify({ t: GNA_GRUNNBILDE_TYPE, v: GNA_GRUNNBILDE_VERSJON, op: 'delete', id });
}
/**
 * Parser en konvolutt (GNÅ-siden, og til test her).
 *
 * `null` for alt som ikke er vår type/versjon — en fremmed melding i rommet skal
 * ikke velte innlesningen.
 */
export function parseGnaGrunnbilde(raa) {
    let o = raa;
    if (typeof raa === 'string') {
        try {
            o = JSON.parse(raa);
        }
        catch {
            return null;
        }
    }
    // ⚠ NULL-VAKTA ER IKKE PYNT. Uten den kastet funksjonen på `null` og på
    // `"null"` — og en tom eller ødelagt melding i rommet ville da veltet HELE
    // innlesningsløkka i GNÅ, ikke bare hoppet over den ene meldingen. En test
    // fanget det: nettopp egenskapen doc-kommentaren lover.
    if (o === null || typeof o !== 'object')
        return null;
    const b = o;
    if (b.t !== GNA_GRUNNBILDE_TYPE)
        return null;
    // ⚠ AVVISER NYERE VERSJON. En klient som «prøver så godt den kan» på et format
    // den ikke kjenner, kan tegne et felt feil — og her betyr feil felt feil
    // usikkerhet. Bedre å vise ingenting og la brukeren oppdatere appen.
    if (b.v !== GNA_GRUNNBILDE_VERSJON)
        return null;
    if (b.op === 'delete') {
        return typeof b.id === 'string' && b.id ? { op: 'delete', id: b.id } : null;
    }
    if (b.op !== 'upsert')
        return null;
    const r = GnaGrunnbildeSchema.safeParse(b.hendelse);
    return r.success ? { op: 'upsert', hendelse: r.data } : null;
}
/**
 * Skal GNÅ tegne hendelsen som et OMRÅDE framfor en pin?
 *
 * ⚠ SANT MED MINDRE VI VET AT DET ER ET PUNKT. Standarden er område, ikke pin: en
 * klient som er i tvil skal tegne usikkerhet. Det er motsatt av den vanlige
 * refleksen (pin er enklere), og det er med vilje — en pin er en påstand.
 *
 * En posisjon et MENNESKE har rettet regnes som punkt, uansett hva radiusen sier:
 * hun pekte.
 */
export function gnaTegnesSomOmraade(h) {
    // ⚠ EN KJENT FLATE TEGNES ALLTID SOM FLATE, også når noen har «rettet» den.
    // Rettingen gjelder ankeret, ikke geometrien: MET sitt polygon er ikke noe en
    // operatør kan flytte ved å dra en markør.
    if (h.presisjon === 'omraade')
        return true;
    if (h.rettet_av)
        return false;
    if (h.presisjon === 'punkt')
        return false;
    return true;
}
/**
 * Er hendelsen utløpt for GNÅ?
 *
 * ⚠ KLIENTEN MÅ SJEKKE SELV, ikke bare stole på at hubben slutter å sende. En
 * telefon som har vært offline i ti dager har en lokal kopi av alt, og uten denne
 * sjekken ville den vist forrige ukes hendelser som om de var nye. Det er samme
 * feilklasse som `hendelseslogg.ts` retter med alder på hver linje.
 */
export function gnaUtloept(h, naaMs) {
    return naaMs >= h.utloper_ms;
}
/**
 * Teksten GNÅ viser under en hendelse med usikker posisjon.
 *
 * ⚠ ORDLAGT FOR EN TELEFON I FELT, ikke for et operatørbord. Kort, uten tall
 * operatøren må regne på, og med det ene som betyr noe: dette er ikke stedet, det er
 * området. Attribusjonen står med fordi NLOD krever den og fordi kilden er en del av
 * hvor mye man skal tro på meldingen.
 */
export function gnaUsikkerhetsTekst(h) {
    if (h.rettet_av)
        return `Sted satt av ${h.rettet_av} · ${h.kilde}`;
    if (h.presisjon === 'punkt')
        return h.kilde;
    const km = h.usikkerhet_m >= 1000 ? `${Math.round(h.usikkerhet_m / 1000)} km` : `${Math.round(h.usikkerhet_m)} m`;
    return `⚠ Omtrentlig sted — et sted innenfor ${km} · ${h.kilde}`;
}
//# sourceMappingURL=grunnbilde-gna.js.map